import {Component, HostListener, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation} from '@angular/core';
import {FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslationService} from '../../../../core/services/translation.service';
import {BaseDataService} from '../../../../core/services/base-data.service';
import {BaseData} from '../../../../data/models/base-data.model';
import {AddressSearchService} from '../../../../core/services/address-search.service';
import {AddressSearch} from '../../../../data/models/address-search.model';
import {LocaleService} from '../../../../core/services/locale.service';
import {CustomValidators} from '../../../../core/validators';
import {ClientType} from "../../../../data/models/client-type.model";
import {AddressCode} from "../../../../data/models/address-code.model";
import {DateAdapter, MatOption, provideNativeDateAdapter} from '@angular/material/core';
import {MatCheckbox, MatCheckboxChange} from '@angular/material/checkbox';
import {MatError, MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatInput} from "@angular/material/input";
import {MatSelect} from "@angular/material/select";

import {NgxMaskDirective} from "ngx-mask";
import {MatAutocomplete, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {DatePickerFormatDirective} from "../../../../core/directives/date-picker-format.directive";
import {
  HupSubscriptionAddressSpecificsComponent
} from "./hup-subscription-address-specifics/hup-subscription-address-specifics.component";
import {ConfigService} from "../../../../core/services/config.service";
import {
  AddressFieldHandler,
  AddressFormularField,
  CustomAddressConfigHolder
} from "./hup-subscription-address-specifics/address-field-handler";
import {Offer} from "../../../../data/models/offer.model"
import {CustomerType} from "../../../../core/models/customer-type";
import {NgClass} from "@angular/common";
import {HttpClient} from '@angular/common/http';
import {MatIcon} from '@angular/material/icon';

@Component({
  selector: 'app-hup-subscription-address[controlGroup]',
  templateUrl: './hup-subscription-address.component.html',
  styleUrls: ['./hup-subscription-address.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    HupSubscriptionAddressSpecificsComponent,
    ReactiveFormsModule,
    MatCheckbox,
    MatFormField,
    MatDatepickerInput,
    MatInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatSelect,
    MatOption,
    NgxMaskDirective,
    MatAutocomplete,
    MatAutocompleteTrigger,
    DatePickerFormatDirective,
    MatError,
    MatLabel,
    MatHint,
    MatSuffix,
    NgClass,
    MatIcon
  ],
  providers: [provideNativeDateAdapter()],
  standalone: true
})
export class HupSubscriptionAddressComponent implements OnInit, OnDestroy, OnChanges {

  translationService = inject(TranslationService);
  private baseDataService = inject(BaseDataService);
  private addressSearchService = inject(AddressSearchService);
  private localeService = inject(LocaleService);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private configService = inject(ConfigService);
  protected addressFieldHandler  = inject(AddressFieldHandler);
  private httpClient = inject(HttpClient);

  @Input() showActivateCheckbox = false;
  @Input() showSalutation = true;
  @Input() showAddressExt1 = true;
  @Input() showTitle = true;
  @Input() askForBirthday = false;
  @Input() isBirthdayRequired = false;
  @Input() askForEmail = false;
  @Input() showValidFrom = false;
  @Input() askForPhone = false;
  @Input() isPhoneRequired = false;
  @Input() activateCheckboxText = 'activateCheckboxText';
  @Input() controlGroup: FormGroup;
  @Input() minDate: Date = new Date();
  @Input() isMobile = false;
  @Input() askForCountry = true;
  @Input() askForPerson = true;
  @Input() isReadOnly: boolean = false;
  @Input() forProductType?: Offer;
  @Input() identification?: boolean = false;
  @Input() customerType?: CustomerType; // 0-Privatkunde, 1-Firmenkunde
  @Input() addressType?: string; //zum Anzeigen von dem Adresstyp (Rechnungsadresse, Lieferadresse, etc.)

  clientWithoutAddressAllowed?:boolean = false;
  private customConfig: CustomAddressConfigHolder = new CustomAddressConfigHolder();

  salutations: AddressCode[] = [];
  clientTypes: ClientType[] = [];
  countries: BaseData[] = [];
  titles: BaseData[] = [];

  zipcodes: string[] = [];
  streetNames: string[] = [];
  cities: string[] = [];
  defaultCountry: string;
  addressPartSequence: string ='0';

  addressSearch: AddressSearch = new AddressSearch(null, null, null, null, null, null);

  earliestBirthdateForSubscription: Date;
  minimalAllowedUserAge = 18;

  constructor() {
    this.dateAdapter.getFirstDayOfWeek = () => 1;
  }

  ngOnInit(): void {
    this.clientWithoutAddressAllowed = this.configService.getConfig('client.personWithoutAddressAllowed')?.asBoolean();

    this.customConfig.setBirthdayRequired(this.isBirthdayRequired);
    this.customConfig.setPhoneRequired(this.isPhoneRequired);

    if (this.forProductType) {
      this.addressFieldHandler.setProductType(this.forProductType.offerType.ePaper ? 'digital' : 'print');
    } else {
      this.addressFieldHandler.setProductType('print');
    }

    this.addressFieldHandler.setClientWithoutAddressAllowed(this.clientWithoutAddressAllowed);

    this.countries = this.baseDataService.getBaseData('countrycodes');
    const locale = this.localeService.getDefaultCountry();
    const country = locale.split('_');

    this.countries?.sort((a, b) => {
      const textA = a.description.toUpperCase();
      const textB = b.description.toUpperCase();
      return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
    });

    this.countries?.forEach(_country => {
      if (_country.key === country[0].toUpperCase()) {
        this.defaultCountry = _country.key;
        if (!this.controlGroup.get('address.country').getRawValue() || this.controlGroup.get('address.country').getRawValue() === '') {
          this.controlGroup.get('address.country').setValue(_country, {emitEvent: true});
        }
      }
    })
    const _addressPartSequence = this.configService.getConfig('address.addresspart.sequence')
    if (_addressPartSequence){
      this.addressPartSequence = _addressPartSequence.value;
    }

    if (this.defaultCountry === 'NL') {
      if (!this.showActivateCheckbox) {
        this.controlGroup.get('person').get('initials').setValidators(Validators.required);
        this.controlGroup.get('person').get('firstName').clearValidators();
      }
    } else {
      //this.controlGroup.get('person').get('firstName').setValidators(Validators.required);
      this.controlGroup.get('address.street').valueChanges.subscribe(value => {
        this.addressSearch.streetName = value;
        this.unsetCityAndStreet();
        this.searchStreet();
      });

      this.controlGroup.get('address.city').valueChanges.subscribe(value => {
        this.addressSearch.city = value;
        this.unsetCityAndStreet();
        this.searchCity();
      });
      if(!this.showActivateCheckbox && this.showSalutation) {
        this.onSalutationChange('')
      }
      this.controlGroup.get('person.salutation').valueChanges.subscribe(value => {
        this.onSalutationChange(value?.key);
      });
    }

    this.setPhoneValidator();

    this.salutations = this.baseDataService.getBaseData('addresscodes');
    this.clientTypes = this.baseDataService.getBaseData('clienttypes');
    this.titles = this.baseDataService.getBaseData('titles');

    this.controlGroup.get('address.zipcode').addValidators([
      Validators.pattern(/[^A-Za-z0-9]*/g),
      Validators.maxLength(10)
    ]);
    this.controlGroup.get('address.houseNo').addValidators([
      Validators.pattern(/[^A-Za-z0-9\-]*/g),
      Validators.maxLength(10)
    ]);

    // Nur umschalten, wenn sich der Zustand wirklich ändert. enable()/disable() laufen rekursiv über
    // alle Kinder und emittieren dabei auf jedem Control ein valueChanges — auch als No-Op, obwohl
    // sich kein Wert geändert hat. Aufrufer, die valueChanges als "Nutzer hat etwas geändert" deuten
    // (z.B. die Identifizierung), verwerfen dadurch ihren Zustand, sobald diese Komponente neu
    // aufgebaut wird (P5W-4383). Beim echten Wechsel readonly <-> editierbar müssen die Events
    // dagegen erhalten bleiben: der Bestell-Step wertet sie über statusChanges aus.
    if (this.isReadOnly) {
      if (this.controlGroup.enabled) {
        this.controlGroup.disable();
      }
    } else if (this.controlGroup.disabled) {
      this.controlGroup.enable();
    }

    console.debug('Register Address Listener');
    this.controlGroup.get('address.street').valueChanges.subscribe(value => {
      this.addressSearch.streetName = value;
      this.searchStreet();
      console.debug('Street Changed');
    });

    this.controlGroup.get('address.zipcode').valueChanges.subscribe(value => {
      if (value || value === '') {
        value = value.toUpperCase();
        this.addressSearch.zipcode = value;
        this.unsetCityAndStreet();
        if (this.defaultCountry === 'NL') {
          this.streetNames = [];
          this.cities = [];
          if (this.addressSearch.houseNo !== null && this.addressSearch.houseNo !== '' && this.addressSearch.zipcode !== null && this.addressSearch.zipcode !== '') {
            this.searchZipCode();
          }
        } else {
          this.searchZipCode();
        }
        console.debug('Zipcode Changed');
      }
    });

    this.controlGroup.get('address.city').valueChanges.subscribe(value => {
      this.addressSearch.city = value;
      this.unsetCityAndStreet();
      this.searchCity();
      console.debug('City Changed');
    });

    this.controlGroup.get('address.houseNo').valueChanges.subscribe(value => {
      this.addressSearch.houseNo = value;
      this.unsetCityAndStreet();
      if (this.defaultCountry === 'NL') {
        this.streetNames = [];
        this.cities = [];
      }
      this.searchAddress();
      console.debug('Houseno Changed');
    });
    console.debug('Registered Address Listener');
    /*
        this.controlGroup.get('address.district').valueChanges.subscribe(value => {
          this.addressSearch.district = value;
          this.searchAddress();
        });
     */

    this.controlGroup.get('showAddress')?.valueChanges.subscribe(value => {
      if (this.defaultCountry === 'NL' && value === true) {
        // validatoren an
        this.controlGroup.get('person').get('initials').setValidators(Validators.required);
      } else if (value === false) {
        // validatoren aus
        this.controlGroup.get('person').get('initials').clearValidators();
        this.controlGroup.reset();
      }
    });

    const _minimalAllowedUserAge = this.configService.getConfig('subscription.minimal-user-age')
    if (_minimalAllowedUserAge) {
      this.minimalAllowedUserAge = parseInt(_minimalAllowedUserAge.value);
    }
    this.handleEarliestBirthdayForSubscription(this.minimalAllowedUserAge);
  }

  ngOnDestroy(): void {
    if (this.forProductType) {
      this.forProductType = null;
    }
    if (this.addressFieldHandler) {
      this.addressFieldHandler.reset();
    }
  }

  @HostListener('focusout', ['$event'])
  onInputBlur(event: FocusEvent) {
    const target = event.target as HTMLInputElement;
    if (target && target.tagName === 'INPUT') {
      this.trimAllFormControls(this.controlGroup);
    }
  }

  trimAllFormControls(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        if (control instanceof FormGroup) {
          this.trimAllFormControls(control);
        } else {
          if (typeof control.value === 'string') {
            const trimmed = control.value.trim();
            // Nur bei echter Änderung schreiben: setValue() führt über updateValueAndValidity() auch
            // die asynchronen Validatoren aus - {emitEvent: false} unterdrückt nur die Events, nicht
            // die Validierung. Ein No-Op-setValue wäre beim Telefonfeld ein HTTP-Request je Feldwechsel.
            if (trimmed !== control.value) {
              control.setValue(trimmed, { emitEvent: false });
            }
          }
        }
      }
    });
  }

  private unsetCityAndStreet() {
    if (this.defaultCountry === 'NL') {
      this.controlGroup.get('address.city')?.setValue(null, {emitEvent: false});
      this.controlGroup.get('address.street')?.setValue(null, {emitEvent: false});
    }
    if (this.controlGroup.get('address.street')?.disabled) {
      this.addressSearch.streetName = null;
    }
    if (this.controlGroup.get('address.city')?.disabled) {
      this.addressSearch.city = null;
    }
  }

  searchStreet(): void {
    if (this.defaultCountry !== 'NL') {
      this.addressSearchService.searchStreet(this.addressSearch).subscribe(searchResult => {
        this.streetNames = [];
        this.addressSearch.zipcode = this.addressSearch?.zipcode?.trim();
        if (searchResult) {
          searchResult.forEach(result => {
            this.streetNames.push(result.streetName);
          });
        }
        this.streetNames = [...new Set(this.streetNames)];
        this.enableDisableFields();
        // Abgeleiteter Wert aus der Suchantwort, keine Nutzereingabe -> darf keine Wert-Listener wecken.
        this.resetDistrict(searchResult[0]?.district);
        console.log(`SEARCH STREET: DISTRICT = ${searchResult[0]?.district}`);
      });
    }
  }

  searchZipCode(): void {
    this.addressSearchService.searchZipcode(this.addressSearch).subscribe(searchResult => {
      this.zipcodes = [];
      this.addressSearch.zipcode = this.addressSearch?.zipcode?.trim();
      if (searchResult) {
        searchResult.forEach(result => {
          this.zipcodes.push(result.zipcode);
        });
      }
      this.zipcodes = [...new Set(this.zipcodes)];
      this.enableDisableFields();
      this.resetDistrict(searchResult[0]?.district);
    });
  }

  searchCity(): void {
    if (this.defaultCountry !== 'NL') {
      this.addressSearchService.searchCity(this.addressSearch).subscribe(searchResult => {
        this.cities = [];
        this.addressSearch.zipcode = this.addressSearch.zipcode?.trim();
        if (searchResult) {
          searchResult.forEach(result => {
            this.cities.push(result.city);
          });
        }
        this.cities = [...new Set(this.cities)];
        this.enableDisableFields();
        this.resetDistrict(searchResult[0]?.district);
        console.log(`SEARCH CITY : DISTRICT = ${searchResult[0]?.district}`);
      });
    }
  }

  searchAddress(): void {
    if (this.addressSearch.city || this.addressSearch.streetName || this.addressSearch.zipcode) {
      if (this.defaultCountry === 'NL') {
        this.addressSearch.streetName = null;
        this.addressSearch.city = null;
        this.addressSearch.district = null;
        this.addressSearchService.searchAddress(this.addressSearch).subscribe(searchResult => {
          this.streetNames = [];
          this.cities = [];
          this.zipcodes = [];
          this.addressSearch.zipcode = this.addressSearch.zipcode?.trim();
          if (searchResult) {
            searchResult.forEach(result => {
              this.streetNames.push(result.streetName);
              this.cities.push(result.city);
              this.zipcodes.push(result.zipcode);
            });
          }
          this.streetNames = [...new Set(this.streetNames)];
          this.cities = [...new Set(this.cities)];
          this.zipcodes = [...new Set(this.zipcodes)];
          this.enableDisableFields();
        });
      }
    }
  }


  private enableDisableFields(searchStreet: boolean = false, searchCity: boolean = false) {
    if (this.defaultCountry === 'NL') {
      if (this.streetNames.length == 1) {

        this.controlGroup.get('address.street')?.setValue(this.streetNames[0], {emitEvent: false});
        this.controlGroup.get('address.street')?.disable({emitEvent: false});
      } else if (!searchStreet) {

        this.controlGroup.get('address.street')?.enable({emitEvent: false});
        this.controlGroup.get('address.street')?.setValue(null, {emitEvent: false});
      }
      if (this.cities.length == 1) {

        this.controlGroup.get('address.city')?.setValue(this.cities[0], {emitEvent: false});
        this.controlGroup.get('address.city')?.disable({emitEvent: false});
      } else if(!searchCity) {

        this.controlGroup.get('address.city')?.enable({emitEvent: false});
        this.controlGroup.get('address.city')?.setValue(null, {emitEvent: false});
      }
    }
  }

  compareBaseData(op1: BaseData, op2: any) {
    return op1.key === op2 || op1.key === op2?.key;
  }

  printForm(): void {
    console.log(this.controlGroup);
  }

  replaceInvalidCharactersFromZipcode() {
    let value = this.controlGroup.get('address.zipcode').value.toUpperCase();
    if (value.length > 10) value = value.slice(0, 10);
    this.controlGroup.get('address.zipcode').setValue(value.replace(/[^A-Za-z0-9]*/g, ''), {emitEvent: false});
  }

  watchInitials(event) {
    const initialsControl = this.controlGroup.get('person.initials');
    let value = initialsControl.value.toUpperCase();
    if (event.keyCode == 8) {
      value = value.substring(0, value.length - 1);
    }
    value = value.replaceAll(/[^a-z]/ig, '').split('').join('.');
    value += value.length ? '.' : '';
    initialsControl.setValue(value, {emitEvent: false});
  }

  /**
   * Setzt den Validator für die Telefonnummer. Validiert wird gegen
   * "/webabo/users/phone/validate" (Google LibPhoneNumber)  mittels asynchronem Validator.
   */
  setPhoneValidator() {
    if (this.askForPhone) {
      let lastValidatedResult: any = null;
      this.controlGroup.get('userData.phone').setAsyncValidators(
        CustomValidators.validatePhoneNumber(
          this.httpClient, this.translationService,
          this.controlGroup.get('country')?.value?.key || this.defaultCountry || 'D',
          (result) => {
            lastValidatedResult = result;
          }
        )
      );

      this.controlGroup.get('userData.phone').valueChanges.subscribe(value => {
        if (!value) {
          return;
        }
        const phoneControl = this.controlGroup.get('userData.phone');
        if (phoneControl.valid && value !== lastValidatedResult) {
          phoneControl.patchValue(lastValidatedResult, { emitEvent: false });
        }
      });
    }
  }

  protected readonly document = typeof document === 'object' ? document : null;

  onSalutationChange(salutation: string) {
    if(this.controlGroup.get('showAddress')?.value === true) {
      this.controlGroup.get('person.firstName').addValidators(Validators.required);
    }
    if(salutation && salutation != '') {
      this.salutations.forEach(ac => {
        if(ac.key == salutation) {
          this.clientTypes.forEach( ct => {
            if (ct.defaultAddressCodeId && ct.defaultAddressCodeId === ac.key) {
              if (ct.justOneNameField) {
                this.controlGroup.get('person.firstName').clearValidators();
              } else {
                this.controlGroup.get('person.firstName').addValidators(Validators.required);
              }
            }
          });
        }
      });
    }
    // Nur Validatoren auffrischen — der Wert ändert sich hier nicht. Ohne emitEvent: false emittiert
    // updateValueAndValidity() ein valueChanges, das bis zur Adress-Gruppe hochblubbert; connectForm
    // deutet das als Wertänderung und überschreibt die bereits geladene Adresse im OrderState (P5W-4383).
    this.controlGroup.get('person.firstName').updateValueAndValidity({emitEvent: false});
  }

  private handleEarliestBirthdayForSubscription(minAge: number) {
    const today = new Date();
    this.earliestBirthdateForSubscription = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());
  }

  isRequired(fieldName: AddressFormularField) {
    return this.addressFieldHandler.isRequired(fieldName, this.customConfig);
  }

  resetFields(event: MatCheckboxChange) {
    if (!event.checked) {
      this.controlGroup.reset();
      this.controlGroup.get('address.country')?.setValue(this.defaultCountry, { emitEvent: true });
      this.controlGroup.get('showAddress').enable();
      this.controlGroup.clearValidators();
      this.controlGroup.clearAsyncValidators();
    }
  }

  /**
   * Da die Notwendigkeit, Bonität zu prüfen, erst nach der Auswahl des Bonus festgelegt wird
   * (d. h. nach Initialisierung der Komponente),
   * muss hier auf Änderungen der Konfiguration reagiert werden.
   * Der initiale Wert kann sich somit unterscheiden.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['isBirthdayRequired']) {
      this.customConfig.setBirthdayRequired(changes['isBirthdayRequired'].currentValue);
    }

    if (changes['isPhoneRequired']) {
      this.customConfig.setPhoneRequired(changes['isPhoneRequired'].currentValue);
    }

    if (changes['askForBirthday']) {
      //falls Geburtstag vorhanden ist & dargestellt wird, muss er deaktiviert werden
      if (this.askForBirthday && !this.controlGroup.get('userData.birthDay').value) {
        // emitEvent: false — das Feld wird nur freigeschaltet, sein Wert bleibt leer. Der sonst
        // entstehende No-Op-Emit landet über connectForm als Wertänderung im OrderState.
        this.controlGroup.get('userData.birthDay').enable({emitEvent: false});
      }
    }
  }

  private resetDistrict(value: string) {
    this.controlGroup.get('address.district').setValue(value, {emitEvent: false});
  }

  protected readonly AddressFormularField = AddressFormularField;
  protected readonly CustomerType = CustomerType;
}
